import {ActionList, ActionMenu, Button} from '@primer/react'
import {GitHubAvatar} from '@github-ui/github-avatar'
import {useClickAnalytics} from '@github-ui/use-analytics'
import type {Location} from '@github-ui/react-router'
import {ANALYTICS_CATEGORY} from '../constants'
import type {SsoOrganization} from '../types'

export interface SSOActionMenuProps {
  ssoOrgs: SsoOrganization[]
  redirectLocation: Location
}

export function generateSSOUrl(id: string, location: Location): string {
  const baseUrl = `/orgs/${encodeURIComponent(id)}/sso`
  const returnTo = location.pathname + location.search + location.hash
  return `${baseUrl}?return_to=${encodeURIComponent(returnTo)}`
}

export function SSOActionMenu({ssoOrgs, redirectLocation}: SSOActionMenuProps) {
  const {sendClickAnalyticsEvent} = useClickAnalytics()

  if (ssoOrgs.length === 0) {
    return null
  }

  const handleSingleSsoClick = () => {
    sendClickAnalyticsEvent({
      category: ANALYTICS_CATEGORY,
      action: 'click_single_sso_button',
      label: 'sso_authentication',
    })
  }

  const handleSsoDropdownItemClick = (orgName: string) => {
    sendClickAnalyticsEvent({
      category: ANALYTICS_CATEGORY,
      action: 'click_sso_dropdown_item',
      label: `org:${orgName}`,
    })
  }

  if (ssoOrgs.length === 1) {
    const org = ssoOrgs[0]

    if (org === undefined) {
      return null
    }

    return (
      <Button as={'a'} href={generateSSOUrl(org.login, redirectLocation)} onClick={handleSingleSsoClick}>
        Single sign-on
      </Button>
    )
  }

  return (
    <ActionMenu>
      <ActionMenu.Button size="small">Single sign-on</ActionMenu.Button>
      <ActionMenu.Overlay>
        <ActionList>
          {ssoOrgs.map(org => (
            <ActionList.LinkItem
              key={org.login}
              href={generateSSOUrl(org.login, redirectLocation)}
              onClick={() => handleSsoDropdownItemClick(org.name)}
            >
              {org.avatar_url && (
                <ActionList.LeadingVisual>
                  <GitHubAvatar size={24} src={org.avatar_url} />
                </ActionList.LeadingVisual>
              )}
              {org.name}
            </ActionList.LinkItem>
          ))}
        </ActionList>
      </ActionMenu.Overlay>
    </ActionMenu>
  )
}

try{ SSOActionMenu.displayName ||= 'SSOActionMenu' } catch {}